import React, { useEffect, useState } from 'react';
import { TrackingPageView } from '../tracking/TrackingPageView';
import { SearchScreenView } from '../tracking/SearchScreenView';
import { DEFAULT_CONFIG, fixtureTrackingPageData } from '../tracking/fixtures';
import { TrackingPageConfig, TrackingStatus } from '../tracking/types';

/**
 * Preview target for the merchant editor in app.shipaid.com.
 *
 * The editor frames this route and pushes config over `postMessage`, so the
 * preview is the real tracking page rather than a second implementation that
 * drifts from it. It renders fixture data, so it works before the merchant has
 * any live shipment.
 */

export const PREVIEW_READY = 'shipaid:tracking-preview:ready';
export const PREVIEW_MESSAGE = 'shipaid:tracking-preview';
export const PREVIEW_HEIGHT = 'shipaid:tracking-preview:height';

/**
 * Only the merchant admin may drive this page. Anything else posting into the
 * frame is ignored.
 */
export const ALLOWED_PREVIEW_ORIGINS: string[] = [
  'https://app.shipaid.com',
  'https://staging-app.shipaid.com',
  'http://localhost:3000',
];

/** Which shopper-facing screen the editor wants to see. */
export type PreviewScreen = 'result' | 'search';

type PreviewMessage = {
  type?: string;
  config?: TrackingPageConfig;
  status?: TrackingStatus;
  screen?: PreviewScreen;
};

export function PreviewFrame() {
  const [config, setConfig] = useState<TrackingPageConfig>(DEFAULT_CONFIG);
  const [status, setStatus] = useState<TrackingStatus>('IN_TRANSIT');
  const [screen, setScreen] = useState<PreviewScreen>('result');

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!ALLOWED_PREVIEW_ORIGINS.includes(event.origin)) return;

      const message = event.data as PreviewMessage;
      if (!message || message.type !== PREVIEW_MESSAGE) return;

      if (message.config) setConfig(message.config);
      if (message.status) setStatus(message.status);
      if (message.screen) setScreen(message.screen);
    };

    window.addEventListener('message', onMessage);

    // The editor buffers config until it hears this, so it cannot lose the
    // first push to a race with the iframe load.
    window.parent?.postMessage({ type: PREVIEW_READY }, '*');

    // Report our real height so the editor can grow the frame rather than
    // clipping the page into a scrollable box.
    const reportHeight = () =>
      window.parent?.postMessage(
        { type: PREVIEW_HEIGHT, height: document.body.scrollHeight },
        '*',
      );

    reportHeight();
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(reportHeight)
        : null;
    observer?.observe(document.body);

    return () => {
      window.removeEventListener('message', onMessage);
      observer?.disconnect();
    };
  }, []);

  // The shopper pages stretch to `100vh`, which inside a frame means the frame's
  // own height — so the measured height could never shrink below it. Preview
  // mode measures true content instead.
  const previewReset = (
    <style>
      {`[data-testid="tracking-page-root"],[data-testid="search-screen-root"]{min-height:0 !important}
        html,body{margin:0;padding:0}`}
    </style>
  );

  if (screen === 'search') {
    // Inert inputs: the preview is for layout and branding, not interaction.
    return (
      <>
        {previewReset}
        <SearchScreenView
          config={config}
          customDomain="preview"
          emailOrPhone=""
          trackingOrOrderNumber=""
          onEmailOrPhoneChange={() => undefined}
          onTrackingOrOrderNumberChange={() => undefined}
          onSubmit={() => undefined}
        />
      </>
    );
  }

  return (
    <>
      {previewReset}
      <TrackingPageView data={fixtureTrackingPageData(status, config)} />
    </>
  );
}
